// Handover of the session URL between browser tabs.
//
// Some banks return from the authorization flow into a new browser tab. The new
// tab announces itself on a broadcast channel, the original tab replies, and the
// new tab hands its URL over and closes, so the session continues in one tab.

const CHANNEL_NAME = 'new-tab-channel'

/**
 * Listens for the URL of a newly opened tab. Polling must already be stopped.
 *
 * @param {function(string): void} onUrl called with the URL to navigate this tab to
 * @returns {BroadcastChannel} channel which must be closed when the page unmounts
 */
export function receiveUrlFromNewTab (onUrl) {
  const channel = new BroadcastChannel(CHANNEL_NAME)
  const tabId = crypto.randomUUID?.() ?? String(Math.random())
  channel.onmessage = (event) => {
    const data = event.data
    if (data === 'new-tab-opened') {
      channel.postMessage({ type: 'old-tab-received', tabId })
    } else if (data && data.type === 'new-tab-url' && data.targetTabId === tabId) {
      channel.postMessage('url-received')
      onUrl(data.url)
    }
  }
  return channel
}

// A round trip between same-origin tabs is sub-millisecond, so this only delays
// the case where no other tab is listening (a cold landing on '/').
const HANDOFF_WAIT_MS = 400

/**
 * Announces this newly opened tab and hands its URL over to the tab which
 * replies first.
 *
 * The caller must NOT navigate this tab until onHandedOver has been called:
 * growing the session history past one entry makes the tab no longer
 * script-closable, so window.close() would be silently ignored.
 *
 * @param {function(): void} onHandedOver called when this tab is no longer needed
 * @returns {Promise<boolean>} true when a listening tab took over, false when no
 *   tab answered within HANDOFF_WAIT_MS
 */
export function sendUrlToOpenedTab (onHandedOver) {
  return new Promise((resolve) => {
    const channel = new BroadcastChannel(CHANNEL_NAME)
    // Only the tab which replies first is handed the URL.
    let decided = false
    const timer = setTimeout(() => {
      decided = true
      closeNewTabChannel(channel)
      resolve(false)
    }, HANDOFF_WAIT_MS)
    channel.onmessage = ({ data }) => {
      if (data?.type === 'old-tab-received' && !decided) {
        decided = true
        clearTimeout(timer)
        channel.postMessage({ type: 'new-tab-url', url: window.location.href, targetTabId: data.tabId })
        resolve(true)
      } else if (data === 'url-received') {
        closeNewTabChannel(channel)
        onHandedOver()
      }
    }
    channel.postMessage('new-tab-opened')
  })
}

/**
 * @param {BroadcastChannel} channel channel to close, may be unset
 * @returns {null} value to reset the channel reference with
 */
export function closeNewTabChannel (channel) {
  if (channel) {
    channel.onmessage = null
    channel.close()
  }
  return null
}
